import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { authenticateUser } from "@surgia/api/src";
import { getKeycloakUser, getKeycloakTokens } from "../utils/keyclock";

interface AuthenticationResult {
  hasAccess: boolean;
  userInfo?: any;
  errorMessageKey?: string;
}

interface UseSignInReturn {
  handleSignIn: () => Promise<AuthenticationResult | undefined>;
  isProcessing: boolean;
}

export const useSignIn = (): UseSignInReturn => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSignIn = useCallback(async (): Promise<
    AuthenticationResult | undefined
  > => {
    setIsProcessing(true);

    try {
      // Set token from Keycloak user
      const keycloakUser = getKeycloakUser();
      if (keycloakUser?.access_token) {
        localStorage.setItem("token", keycloakUser.access_token);
      }

      // Handle invitation token if present
      const invitationToken = sessionStorage.getItem("invitation_token");
      if (invitationToken) {
        sessionStorage.removeItem("invitation_token");
      }

      const keycloakTokens = getKeycloakTokens();

      // Authenticate user
      const authResult: AuthenticationResult =
        await authenticateUser(keycloakTokens);

      // Store user data in localStorage
      localStorage.setItem(
        "permissions",
        JSON.stringify(
          authResult.userInfo?.user_permissions?.map((p: any) => p.name) || []
        )
      );
      localStorage.setItem(
        "tenant_id",
        JSON.stringify(authResult?.userInfo?.user_profile?.tenant?.id)
      );
      localStorage.setItem(
        "user_groups",
        JSON.stringify(authResult?.userInfo?.user_info?.user_groups)
      );
      localStorage.setItem(
        "branches",
        JSON.stringify(
          authResult?.userInfo?.user_info?.user_branches?.map(
            (branch: { id: number }) => branch.id
          ) || []
        )
      );
      return authResult;
    } catch (error: any) {
      console.error("Authentication error:", error);
      // Handle authentication errors
      localStorage.removeItem("token");
      sessionStorage.clear();
      return undefined;
    } finally {
      setIsProcessing(false);
    }
  }, [dispatch]);

  return {
    handleSignIn,
    isProcessing,
  };
};
